import { useModelStore } from "@/store";
import { BaseTool } from "./BaseTool";

// Import backend provider services
import { BackendProviderService } from "@/lib/ai-providers/backend-provider-service";
import BackendProviderWrapper from "@/lib/ai-providers/backend-provider-wrapper";

// Global canvas state manager for tools
let globalCanvasState = {
  isOpen: false,
  content: "",
  type: "text",
  language: "javascript",
  isDirty: false,
  metadata: {},
};

// Global canvas event emitter for communication with React components
const canvasEventEmitter = {
  listeners: new Map(),
  emit(event, data) {
    const listeners = this.listeners.get(event) || [];
    listeners.forEach((callback) => callback(data));
  },
  on(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event).push(callback);
  },
  off(event, callback) {
    const listeners = this.listeners.get(event) || [];
    const index = listeners.indexOf(callback);
    if (index > -1) {
      listeners.splice(index, 1);
    }
  },
};

// Export the event emitter for React components to use
export { canvasEventEmitter };

// Initialize backend provider service
let backendProviderService = null;

// Configuration flag to use backend providers
const USE_BACKEND_PROVIDERS = true;

/**
 * Initialize backend provider service if needed
 */
async function initializeBackendService() {
  if (!backendProviderService && USE_BACKEND_PROVIDERS) {
    try {
      backendProviderService = new BackendProviderService();
    } catch (error) {
      console.error("🎨 Failed to initialize backend provider service:", error);
    }
  }
}

/**
 * Create a provider wrapper for the specified provider
 * @param {string} providerName - Name of the provider
 * @param {Object} options - Provider options
 * @returns {BackendProviderWrapper} - Provider wrapper instance
 */
function createProviderWrapper(providerName, options = {}) {
  return new BackendProviderWrapper(providerName, options);
}

/**
 * Get current model and provider from model store
 * @returns {Object} - Object containing model, provider, and store info
 */
function getCurrentModelInfo(assistantModel) {
  try {
    // If we have an assistant model passed in, use that first
    if (assistantModel) {
      const provider = determineProviderFromModel(assistantModel);
      return {
        model: assistantModel,
        provider,
        store: null,
      };
    }

    // Dynamic import to avoid circular dependencies
    const modelStore = useModelStore.getState();
    const currentModel = modelStore.model || modelStore.selectedModel;
    const currentProvider = modelStore.provider || modelStore.selectedProvider;

    if (!currentModel) {
      console.warn(
        "🎨 No current model found in model store, skipping canvas operation"
      );
      return null; // Return null to indicate no valid model found
    }

    // Use the provider from the store, or determine it from the model
    const provider =
      currentProvider || determineProviderFromModel(currentModel);

    return {
      model: currentModel,
      provider,
      store: modelStore,
    };
  } catch (error) {
    // Return null consistently when errors occur, matching the behavior when no model is found
    // This ensures all callers receive null and can handle it appropriately (skip operation or use fallback)
    console.warn("🎨 Error getting model store info, returning null:", error);
    return null;
  }
}

/**
 * Determine provider from model name
 * @param {string} model - Model name
 * @returns {string} - Provider name
 */
function determineProviderFromModel(model) {
  if (!model) return "openai";

  const lowerModel = model.toLowerCase();

  // Anthropic models
  if (lowerModel.includes("claude")) return "anthropic";

  // Google/Gemini models
  if (lowerModel.includes("gemini")) return "gemini";

  // Mistral models
  if (lowerModel.includes("mistral")) return "mistral";

  // DeepSeek models
  if (lowerModel.includes("deepseek")) return "deepseek";

  // XAI models
  if (lowerModel.includes("grok")) return "xai";

  // Qwen models
  if (lowerModel.includes("qwen")) return "qwen";

  // Cohere models
  if (lowerModel.includes("command")) return "cohere";

  // Perplexity models
  if (lowerModel.includes("sonar") || lowerModel.includes("pplx"))
    return "perplexity";

  // Groq models (common model names on Groq)
  if (
    lowerModel.includes("llama") ||
    lowerModel.includes("mixtral") ||
    lowerModel.includes("gemma") ||
    lowerModel.includes("moonshotai") ||
    lowerModel.includes("kimi")
  ) {
    return "groq";
  }

  // OpenRouter models (usually have provider prefix)
  if (lowerModel.includes("/")) return "openrouter";

  // Default to OpenAI for GPT models and unknown models
  return "openai";
}

/**
 * Get provider instance for streaming (now using backend service)
 * @param {string} provider - Provider name
 * @param {string} model - Model name
 * @returns {Object} - Provider instance or backend service info
 */
async function getProviderInstance(provider, model) {
  try {
    // Initialize backend service if configured
    await initializeBackendService();

    if (USE_BACKEND_PROVIDERS && backendProviderService) {
      return {
        isBackendService: true,
        providerName: provider,
        modelName: model,
        backendService: backendProviderService,
      };
    } else {
      return createProviderWrapper(provider, {
        defaultModel: model,
      });
    }
  } catch (error) {
    console.error(`🎨 Error creating ${provider} provider instance:`, error);
    // Fallback to OpenAI backend service
    await initializeBackendService();
    return {
      isBackendService: true,
      providerName: "openai",
      modelName: "gpt-4.1-mini",
      backendService: backendProviderService,
    };
  }
}

/**
 * Get optimal streaming parameters for different providers
 * @param {string} provider - Provider name
 * @param {string} model - Model name
 * @returns {Object} - Optimized parameters for the provider
 */
function getOptimalStreamingParams(provider, model) {
  const baseParams = {
    model: model,
    max_tokens: 4000,
    temperature: 0.7,
  };

  // Provider-specific optimizations
  switch (provider) {
    case "anthropic":
      return {
        ...baseParams,
        max_tokens: 1500, // Anthropic canvas streaming limit
        temperature: 0.7,
      };
    case "deepseek":
      return {
        ...baseParams,
        max_tokens: 4000,
        temperature: 0.8, // DeepSeek works well with slightly higher temperature
      };
    case "gemini":
      return {
        ...baseParams,
        max_tokens: 4000,
        temperature: 0.7,
      };
    case "mistral":
      return {
        ...baseParams,
        max_tokens: 4000,
        temperature: 0.7,
      };
    case "xai":
      return {
        ...baseParams,
        max_tokens: 4000,
        temperature: 0.7,
      };
    case "cohere":
      return {
        ...baseParams,
        max_tokens: 4000,
        temperature: 0.75,
      };
    case "perplexity":
      return {
        ...baseParams,
        max_tokens: 4000,
        temperature: 0.7,
      };
    case "groq":
      return {
        ...baseParams,
        max_tokens: 4000,
        temperature: 0.7,
      };
    case "openrouter":
      return {
        ...baseParams,
        max_tokens: 4000,
        temperature: 0.7,
      };
    default: // openai and others
      return baseParams;
  }
}

/**
 * Detect content type from user query
 */
function detectContentTypeFromQuery(query) {
  const lowerQuery = query.toLowerCase();

  // Spreadsheet-related keywords
  if (
    lowerQuery.includes("spreadsheet") ||
    lowerQuery.includes("table") ||
    lowerQuery.includes("csv") ||
    lowerQuery.includes("data") ||
    lowerQuery.includes("excel") ||
    lowerQuery.includes("sheet") ||
    lowerQuery.includes("rows") ||
    lowerQuery.includes("columns") ||
    lowerQuery.includes("grid") ||
    lowerQuery.includes("database") ||
    lowerQuery.includes("records") ||
    lowerQuery.includes("entries")
  ) {
    return "spreadsheet";
  }

  // Code-related keywords
  if (
    lowerQuery.includes("code") ||
    lowerQuery.includes("function") ||
    lowerQuery.includes("script") ||
    lowerQuery.includes("program") ||
    lowerQuery.includes("algorithm") ||
    lowerQuery.includes("implement")
  ) {
    return "code";
  }

  // HTML-related keywords
  if (
    lowerQuery.includes("html") ||
    lowerQuery.includes("webpage") ||
    lowerQuery.includes("website") ||
    lowerQuery.includes("form") ||
    lowerQuery.includes("page")
  ) {
    return "html";
  }

  // Markdown-related keywords
  if (
    lowerQuery.includes("markdown") ||
    lowerQuery.includes("readme") ||
    lowerQuery.includes("documentation") ||
    lowerQuery.includes("article")
  ) {
    return "markdown";
  }

  return "text";
}

/**
 * Detect programming language from user query
 */
function detectLanguageFromQuery(query) {
  const lowerQuery = query.toLowerCase();

  // Primary supported languages
  if (lowerQuery.includes("python") || lowerQuery.includes("py"))
    return "python";
  if (
    lowerQuery.includes("javascript") ||
    lowerQuery.includes("js") ||
    lowerQuery.includes("node") ||
    lowerQuery.includes("react") ||
    lowerQuery.includes("jsx")
  )
    return "javascript";

  // Secondary supported languages (fallback to javascript for code editor compatibility)
  if (lowerQuery.includes("typescript") || lowerQuery.includes("ts"))
    return "javascript"; // TypeScript uses JS highlighting
  if (lowerQuery.includes("html")) return "html";
  if (lowerQuery.includes("css")) return "css";
  if (lowerQuery.includes("sql")) return "javascript"; // Fallback to JS
  if (lowerQuery.includes("bash") || lowerQuery.includes("shell"))
    return "javascript"; // Fallback to JS
  if (lowerQuery.includes("java")) return "javascript"; // Fallback to JS
  if (lowerQuery.includes("c++") || lowerQuery.includes("cpp"))
    return "javascript"; // Fallback to JS
  if (lowerQuery.includes("c#") || lowerQuery.includes("csharp"))
    return "javascript"; // Fallback to JS
  if (lowerQuery.includes("php")) return "javascript"; // Fallback to JS
  if (lowerQuery.includes("ruby")) return "javascript"; // Fallback to JS
  if (lowerQuery.includes("go") || lowerQuery.includes("golang"))
    return "javascript"; // Fallback to JS
  if (lowerQuery.includes("rust")) return "javascript"; // Fallback to JS

  return "javascript"; // Default fallback - primary supported language
}

/**
 * Clean up content that may be wrapped in markdown code blocks
 */
function cleanMarkdownContent(content) {
  if (!content || typeof content !== "string") return content;

  // Remove markdown code block wrapper if present
  const markdownBlockMatch = content.match(/^```markdown\s*\n([\s\S]*?)\n```$/);
  if (markdownBlockMatch) {
    return markdownBlockMatch[1].trim();
  }

  // Remove any other code block wrapper if present
  const codeBlockMatch = content.match(/^```[\w]*\s*\n([\s\S]*?)\n```$/);
  if (codeBlockMatch) {
    return codeBlockMatch[1].trim();
  }

  return content;
}

/**
 * Create universal markdown streaming for canvas content generation using backend provider service
 * Supports all providers via the backend service (OpenAI, Anthropic, Gemini, Mistral, DeepSeek, XAI, etc.)
 */
async function createMarkdownStream(
  query,
  type,
  language,
  name,
  version,
  sourceMessageId
) {
  try {
    // Get current model and provider from model store
    const modelInfo = getCurrentModelInfo();
    if (!modelInfo) {
      console.warn(
        "🎨 Skipping canvas operation due to invalid model configuration"
      );
      return null;
    }
    const { model, provider, store } = modelInfo;

    // Get provider instance (now using backend service)
    const providerInstance = await getProviderInstance(provider, model);

    // Emit initial canvas stream event with sourceMessageId immediately
    canvasEventEmitter.emit("canvas_stream", {
      content: "",
      type: type,
      isComplete: false,
      isStreaming: true,
      sourceMessageId, // Include sourceMessageId in initial stream event
      provider: providerInstance.providerName || provider,
    });

    const systemPrompt = `You are an expert content creator specializing in ${type} content generation.

Generate ${type} content based on the user's query: "${query}"

${
  type === "code"
    ? `CRITICAL CODE REQUIREMENTS:
1. Return ONLY a single markdown code block - nothing else
2. NO explanatory text before or after the code block
3. NO additional markdown content outside the code block
4. Format: \`\`\`${language === "python" ? "python" : "javascript"}
[complete code here]
\`\`\`
5. Include ALL necessary imports from NPM packages only
6. Use functional components with React hooks
7. Mobile-first responsive design with proper styling
8. Create complete, self-contained, standalone code
9. Professional layouts with proper spacing and typography

Programming language: ${language}

⚠️ CRITICAL SANDBOXED ENVIRONMENT RULES:
This code will run in an isolated sandbox (Sandpack/CodeSandbox). You MUST follow these rules:

DO NOT USE LOCAL IMPORTS - These will fail:
❌ import { Button } from "./components/ui/button"  // NO local paths
❌ import { cn } from "./lib/utils"  // NO local utilities
❌ import anything from "./..." or "../..."  // NO relative imports

ONLY USE NPM PACKAGES - These work:
✅ import React, { useState, useEffect } from "react"
✅ import { motion } from "framer-motion"
✅ import { format } from "date-fns"
✅ import axios from "axios"
✅ import _ from "lodash"

FOR UI COMPONENTS:
Instead of local shadcn/ui components, either:
1. Create inline components using standard HTML elements + inline styles/Tailwind
2. Use available NPM packages if needed
3. Build simple custom components within the same file

Example of creating inline UI:
\`\`\`javascript
function Button({ children, onClick, className = "" }) {
  return (
    <button
      onClick={onClick}
      className={\`px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 \${className}\`}
    >
      {children}
    </button>
  );
}
\`\`\`

AVAILABLE NPM LIBRARIES (import from these):
- react (useState, useEffect, useCallback, etc.)
- framer-motion (for animations)
- date-fns (date formatting)
- axios (HTTP requests)
- lodash (utilities)

STYLING:
- Use inline styles or Tailwind classes directly
- For class utilities, create a simple inline cn function if needed

Return ONLY the code block - no explanations, no additional text, no markdown formatting outside the code block.`
    : type === "spreadsheet"
      ? `CRITICAL SPREADSHEET REQUIREMENTS:
1. Return ONLY CSV data - nothing else
2. NO explanatory text before or after the CSV data
3. NO markdown formatting or code blocks around the CSV
4. First row must be headers/column names
5. Generate realistic sample data that matches the user's request
6. Use proper CSV formatting with commas as separators
7. Include 10-15 rows of sample data (not more than 20 rows)
8. Limit to maximum 10 columns for optimal display
9. Make data relevant and meaningful to the user's query
10. Ensure proper escaping of commas in cell values (use quotes if needed)
11. Create comprehensive data that would be useful in a spreadsheet
12. Start immediately with the header row - no preamble

Example format:
Name,Age,Department,Email,Salary
John Doe,32,Engineering,john@company.com,75000
Jane Smith,28,Marketing,jane@company.com,65000

Return ONLY the CSV data - no explanations, no additional text, no formatting.`
      : `CRITICAL REQUIREMENTS:
1. Return clean markdown content without wrapping it in code blocks
2. Generate complete, well-structured content that directly addresses the query
3. Use appropriate markdown formatting (headers, lists, emphasis, etc.)
4. Make content comprehensive and useful
5. DO NOT wrap the entire response in \`\`\`markdown code blocks`
}

${name ? `Content title/name: ${name}` : ""}

Generate high-quality ${type === "code" ? "code" : "markdown"} content that fully satisfies the user's request.`;

    const messages = [
      { role: "system", content: systemPrompt },
      { role: "user", content: query },
    ];

    let accumulatedContent = "";

    // Handle streaming with callback - Updated for backend protocol
    const onUpdate = (chunk) => {
      let content = "";

      // Handle backend SSE format first
      if (chunk.type && chunk.chunk) {
        // Backend SSE wrapper format: {type: "chunk", sessionId: "...", chunk: {...}}
        if (
          chunk.type === "chunk" &&
          chunk.chunk.choices?.[0]?.delta?.content
        ) {
          content = chunk.chunk.choices[0].delta.content;
        } else if (chunk.type === "error") {
          console.error("🎨 Canvas stream error:", chunk.error);
          return;
        } else if (chunk.type === "complete") {
          console.log("🎨 Canvas stream completed");
          return;
        }
      }
      // Handle direct chunk format (fallback)
      else if (chunk.choices?.[0]?.delta?.content) {
        // OpenAI, Mistral, Groq, XAI, OpenRouter format
        content = chunk.choices[0].delta.content;
      } else if (chunk.delta?.content) {
        // Alternative delta format
        content = chunk.delta.content;
      } else if (chunk.content) {
        // Direct content format
        content = chunk.content;
      } else if (typeof chunk === "string") {
        // Raw string content
        content = chunk;
      } else if (chunk.text) {
        // Text field format
        content = chunk.text;
      }

      if (content) {
        accumulatedContent += content;

        // Clean the content before emitting
        const cleanedContent = cleanMarkdownContent(accumulatedContent);

        // Emit streaming update to canvas with sourceMessageId
        canvasEventEmitter.emit("canvas_stream", {
          content: cleanedContent,
          type: type,
          isComplete: false,
          isStreaming: true,
          sourceMessageId, // Include sourceMessageId in stream events
          provider: providerInstance.providerName || provider,
        });
      }
    };

    // Choose streaming method based on configuration
    if (providerInstance.isBackendService) {
      await providerInstance.backendService.createChatCompletionStream(
        {
          provider: providerInstance.providerName,
          model: providerInstance.modelName,
          messages: messages,
          max_tokens: 4000,
          temperature: 0.7,
        },
        onUpdate
      );
    } else {
      await providerInstance.createChatCompletionStream(
        {
          model: model,
          messages: messages,
          max_tokens: 4000,
          temperature: 0.7,
        },
        onUpdate
      );
    }

    // Clean the final content
    const finalCleanedContent = cleanMarkdownContent(accumulatedContent);

    // Emit final complete content with sourceMessageId
    canvasEventEmitter.emit("canvas_stream", {
      content: finalCleanedContent,
      type: type,
      isComplete: true,
      isStreaming: false,
      sourceMessageId, // Include sourceMessageId in final stream event
    });

    // Generate smart title from content (async, don't block)
    console.log("🎨 Starting canvas title generation...", {
      contentLength: finalCleanedContent.length,
      type,
      sourceMessageId,
    });

    generateCanvasTitle(finalCleanedContent, type, query)
      .then((smartTitle) => {
        console.log("🎨 Canvas title generated:", {
          smartTitle,
          sourceMessageId,
        });
        if (smartTitle) {
          canvasEventEmitter.emit("canvas_title_update", {
            title: smartTitle,
            sourceMessageId,
          });
          console.log("🎨 Emitted canvas_title_update event");
        } else {
          console.warn("🎨 No title generated (returned null)");
        }
      })
      .catch((error) => {
        console.error("🎨 Failed to generate canvas title:", error);
      });

    return finalCleanedContent;
  } catch (error) {
    console.error("🎨 Error creating markdown stream:", error);
    throw error;
  }
}

/**
 * Create universal markdown streaming specifically for canvas edits using backend provider service
 * Supports all providers via the backend service (OpenAI, Anthropic, Gemini, Mistral, DeepSeek, XAI, etc.)
 */
async function createMarkdownEditStream(
  query,
  type,
  language,
  name,
  version,
  editQuery,
  editType,
  currentContent,
  sourceMessageId
) {
  try {
    // Get current model and provider from model store
    const modelInfo = getCurrentModelInfo();
    if (!modelInfo) {
      console.warn(
        "🎨 Skipping canvas operation due to invalid model configuration"
      );
      return null;
    }
    const { model, provider } = modelInfo;

    // Get provider instance (now using backend service)
    const providerInstance = await getProviderInstance(provider, model);

    const systemPrompt = `You are an expert content editor specializing in ${type} content editing.

Edit the existing content based on the user's request: "${editQuery}"

Edit type: ${editType}
Content type: ${type}
${language ? `Programming language: ${language}` : ""}

Current content:
${currentContent}

${
  type === "code"
    ? `CRITICAL CODE EDITING REQUIREMENTS:
1. Return ONLY a single markdown code block - nothing else
2. NO explanatory text before or after the code block
3. NO additional markdown content outside the code block
4. Format: \`\`\`${language === "python" ? "python" : "javascript"}
[complete edited code here]
\`\`\`
5. Include ALL necessary imports from NPM packages only
6. Use functional components with React hooks
7. Mobile-first responsive design with proper styling
8. Preserve existing structure where possible, only make the requested changes
9. Create complete, self-contained, standalone code

⚠️ CRITICAL SANDBOXED ENVIRONMENT RULES:
This code will run in an isolated sandbox (Sandpack/CodeSandbox). You MUST follow these rules:

DO NOT USE LOCAL IMPORTS - These will fail:
❌ import { Button } from "./components/ui/button"  // NO local paths
❌ import { cn } from "./lib/utils"  // NO local utilities
❌ import anything from "./..." or "../..."  // NO relative imports

ONLY USE NPM PACKAGES - These work:
✅ import React, { useState, useEffect } from "react"
✅ import { motion } from "framer-motion"
✅ import { format } from "date-fns"
✅ import axios from "axios"
✅ import _ from "lodash"

FOR UI COMPONENTS:
Instead of local shadcn/ui components, either:
1. Create inline components using standard HTML elements + inline styles/Tailwind
2. Use available NPM packages if needed
3. Build simple custom components within the same file

AVAILABLE NPM LIBRARIES (import from these):
- react (useState, useEffect, useCallback, etc.)
- framer-motion (for animations)
- date-fns (date formatting)
- axios (HTTP requests)
- lodash (utilities)

STYLING:
- Use inline styles or Tailwind classes directly
- For class utilities, create a simple inline cn function if needed

Return ONLY the code block with the edited code - no explanations, no additional text, no markdown formatting outside the code block.`
    : type === "spreadsheet"
      ? `CRITICAL SPREADSHEET EDITING REQUIREMENTS:
1. Return ONLY the edited CSV data - nothing else
2. NO explanatory text before or after the CSV data
3. NO markdown formatting or code blocks around the CSV
4. Preserve the header row unless specifically asked to change it
5. Apply the requested changes to the existing data
6. Maintain proper CSV formatting with commas as separators
7. Ensure proper escaping of commas in cell values (use quotes if needed)
8. Keep existing data structure and add/modify/remove as requested
9. Make changes that are logical and consistent with the existing data
10. Return the complete updated CSV dataset
11. Limit to maximum 20 rows and 10 columns for optimal display
12. Start immediately with the header row - no preamble

Apply the requested edit: "${editQuery}"

Return ONLY the complete edited CSV data - no explanations, no additional text, no formatting.`
      : `CRITICAL REQUIREMENTS:
1. Return the COMPLETE edited content as clean markdown
2. DO NOT wrap the entire response in \`\`\`markdown code blocks
3. Preserve existing structure where possible, only make the requested changes
4. Use appropriate markdown formatting (headers, lists, emphasis, etc.)
5. Make intelligent edits that improve the content while addressing the user's request`
}

Apply the requested changes thoughtfully and comprehensively. Return the full updated ${
      type === "code"
        ? "code in a single code block"
        : "content as clean markdown"
    }.`;

    const messages = [
      { role: "system", content: systemPrompt },
      { role: "user", content: editQuery },
    ];

    let accumulatedContent = "";

    // Handle streaming with callback
    const onUpdate = (chunk) => {
      let content = "";

      // Handle backend SSE format first
      if (chunk.type && chunk.chunk) {
        // Backend SSE wrapper format: {type: "chunk", sessionId: "...", chunk: {...}}
        if (
          chunk.type === "chunk" &&
          chunk.chunk.choices?.[0]?.delta?.content
        ) {
          content = chunk.chunk.choices[0].delta.content;
        } else if (chunk.type === "error") {
          console.error("🎨 Canvas edit stream error:", chunk.error);
          return;
        } else if (chunk.type === "complete") {
          console.log("🎨 Canvas edit stream completed");
          return;
        }
      }
      // Handle direct chunk format (fallback)
      else if (chunk.choices?.[0]?.delta?.content) {
        // OpenAI, Mistral, Groq, XAI, OpenRouter format
        content = chunk.choices[0].delta.content;
      } else if (chunk.delta?.content) {
        // Alternative delta format
        content = chunk.delta.content;
      } else if (chunk.content) {
        // Direct content format
        content = chunk.content;
      } else if (typeof chunk === "string") {
        // Raw string content
        content = chunk;
      } else if (chunk.text) {
        // Text field format
        content = chunk.text;
      }

      if (content) {
        accumulatedContent += content;

        // Clean the content before emitting
        const cleanedContent = cleanMarkdownContent(accumulatedContent);

        // Emit streaming update specifically for canvas edits
        canvasEventEmitter.emit("canvas_stream_update", {
          content: cleanedContent,
          isComplete: false,
          isStreaming: true,
          editQuery,
          editType,
          version,
          sourceMessageId,
          provider: providerInstance.providerName || provider,
        });
      }
    };

    // Choose streaming method based on configuration
    if (providerInstance.isBackendService) {
      await providerInstance.backendService.createChatCompletionStream(
        {
          provider: providerInstance.providerName,
          model: providerInstance.modelName,
          messages: messages,
          max_tokens: 4000,
          temperature: 0.7,
        },
        onUpdate
      );
    } else {
      await providerInstance.createChatCompletionStream(
        {
          model: model,
          messages: messages,
          max_tokens: 4000,
          temperature: 0.7,
        },
        onUpdate
      );
    }

    // Clean the final content
    const finalCleanedContent = cleanMarkdownContent(accumulatedContent);

    // Generate smart title from content (async, don't block)
    console.log("🎨 Starting canvas title generation...", {
      contentLength: finalCleanedContent.length,
      type,
      sourceMessageId,
    });

    generateCanvasTitle(finalCleanedContent, type, query)
      .then((smartTitle) => {
        console.log("🎨 Canvas title generated:", {
          smartTitle,
          sourceMessageId,
        });
        if (smartTitle) {
          canvasEventEmitter.emit("canvas_title_update", {
            title: smartTitle,
            sourceMessageId,
          });
          console.log("🎨 Emitted canvas_title_update event");
        } else {
          console.warn("🎨 No title generated (returned null)");
        }
      })
      .catch((error) => {
        console.error("🎨 Failed to generate canvas title:", error);
      });

    // Emit final complete content for edits
    canvasEventEmitter.emit("canvas_stream_complete", {
      content: finalCleanedContent,
      isComplete: true,
      isStreaming: false,
      editQuery,
      editType,
      version,
      sourceMessageId,
    });

    return finalCleanedContent;
  } catch (error) {
    console.error("🎨 Error creating markdown edit stream:", error);
    throw error;
  }
}

/**
 * Generate a placeholder name from query (extracts subject from query)
 */
function generateNameFromQuery(query, type) {
  if (!query || typeof query !== "string") {
    const prefix =
      type === "code"
        ? "Code"
        : type === "spreadsheet"
          ? "Spreadsheet"
          : "Document";
    return `${prefix} ${Date.now()}`;
  }

  // Common filler words and action verbs to remove
  const fillerWords = new Set([
    "a",
    "an",
    "the",
    "write",
    "create",
    "generate",
    "make",
    "build",
    "paragraph",
    "document",
    "text",
    "about",
    "for",
    "in",
    "on",
    "code",
    "spreadsheet",
    "canvas",
    "please",
    "can",
    "you",
    "me",
    "some",
    "new",
    "using",
    "with",
  ]);

  // Extract meaningful words, preserving word order and capitalization
  let cleanedQuery = query.trim();

  // Remove common prefixes like "write a paragraph about" or "create a document on"
  cleanedQuery = cleanedQuery
    .replace(/^(write|create|generate|make|build)\s+(a|an|the)\s+/i, "")
    .replace(/^(write|create|generate|make|build)\s+/i, "")
    .replace(
      /\s+(in|on|about|for)\s+(a|an|the)\s+(paragraph|document|canvas|code|spreadsheet)$/i,
      ""
    )
    .replace(/\s+(paragraph|document|canvas|code|spreadsheet)$/i, "");

  // Split into words and filter
  const words = cleanedQuery
    .split(/\s+/)
    .filter((word) => {
      const lowerWord = word.toLowerCase();
      return word.length > 1 && !fillerWords.has(lowerWord);
    })
    .slice(0, 6); // Take up to 6 words for the title

  if (words.length === 0) {
    const prefix =
      type === "code"
        ? "Code"
        : type === "spreadsheet"
          ? "Spreadsheet"
          : "Document";
    return `${prefix} ${Date.now()}`;
  }

  // Capitalize first letter of first word, keep rest as-is for proper nouns
  const titleWords = words.map((word, index) => {
    if (index === 0) {
      return word.charAt(0).toUpperCase() + word.slice(1);
    }
    return word;
  });

  return titleWords.join(" ").substring(0, 50);
}

/**
 * Extract title from content if it exists (H1 heading)
 */
function extractTitleFromContent(content, type) {
  if (!content || typeof content !== "string") return null;

  // For text/markdown content, look for H1 heading at the start
  if (type === "text" || type === "markdown") {
    // Match H1 at the beginning of content (with optional whitespace)
    const h1Match = content.trim().match(/^#\s+(.+?)$/m);
    if (h1Match && h1Match[1]) {
      const title = h1Match[1].trim().substring(0, 50);
      console.log("🎨 Extracted title from H1:", title);
      return title;
    }

    // Also check for HTML h1 tag
    const htmlH1Match = content.match(/<h1[^>]*>(.+?)<\/h1>/i);
    if (htmlH1Match && htmlH1Match[1]) {
      const title = htmlH1Match[1]
        .replace(/<[^>]+>/g, "")
        .trim()
        .substring(0, 50);
      console.log("🎨 Extracted title from HTML h1:", title);
      return title;
    }
  }

  // For code, look for comments at the top
  if (type === "code") {
    const commentMatch = content
      .trim()
      .match(/^(?:\/\/|#|\/\*\*?)\s*(.+?)(?:\*\/)?$/m);
    if (commentMatch && commentMatch[1]) {
      const title = commentMatch[1].trim().substring(0, 50);
      console.log("🎨 Extracted title from code comment:", title);
      return title;
    }
  }

  return null;
}

/**
 * Generate a smart title from canvas content using AI
 */
async function generateCanvasTitle(content, type, _query) {
  try {
    // First, check if content already has a title
    const existingTitle = extractTitleFromContent(content, type);
    if (existingTitle) {
      console.log("🎨 Using existing title from content:", existingTitle);
      return existingTitle;
    }

    // Get current model info
    const modelInfo = getCurrentModelInfo();
    if (!modelInfo) {
      console.warn("🎨 Cannot generate title - no model info");
      return null;
    }
    const { model, provider } = modelInfo;

    // Use a fast, efficient model for title generation
    const titleModel =
      provider === "anthropic" ? "claude-3-5-haiku-20241022" : model;

    const providerInstance = await getProviderInstance(provider, titleModel);

    const prompt = `Generate a 3-4 word title for this ${type === "code" ? "code" : type === "spreadsheet" ? "spreadsheet" : "document"} based on its content.

Guidelines:
- Capture the main topic or purpose
- Be clear and specific
- Use Title Case
- No quotes or punctuation
- Title must be 3-4 words
- For code: include what it does (e.g., "Todo App Component")
- For spreadsheets: include data type (e.g., "Sales Data 2024")
- For text: include subject (e.g., "F-22 Raptor Overview")

Respond ONLY with the title text.

Content preview:
${content.substring(0, 500)}`;

    const messages = [{ role: "user", content: prompt }];

    let titleText = "";

    // Stream the title response
    const onUpdate = (chunk) => {
      let content = "";
      if (chunk.type && chunk.chunk) {
        if (
          chunk.type === "chunk" &&
          chunk.chunk.choices?.[0]?.delta?.content
        ) {
          content = chunk.chunk.choices[0].delta.content;
        }
      } else if (chunk.choices?.[0]?.delta?.content) {
        content = chunk.choices[0].delta.content;
      } else if (chunk.delta?.content) {
        content = chunk.delta.content;
      } else if (typeof chunk === "string") {
        content = chunk;
      }
      if (content) {
        titleText += content;
      }
    };

    if (providerInstance.isBackendService) {
      await providerInstance.backendService.createChatCompletionStream(
        {
          provider: providerInstance.providerName,
          model: titleModel,
          messages: messages,
          max_tokens: 20,
          temperature: 0.3,
        },
        onUpdate
      );
    } else {
      await providerInstance.createChatCompletionStream(
        {
          model: titleModel,
          messages: messages,
          max_tokens: 20,
          temperature: 0.3,
        },
        onUpdate
      );
    }

    // Clean up the title
    const cleanTitle = titleText.trim().replace(/["'`]/g, "").substring(0, 50);
    return cleanTitle || null;
  } catch (error) {
    console.error("🎨 Error generating canvas title:", error);
    return null;
  }
}

/**
 * Canvas Create Tool - Creates new canvas content with markdown streaming
 */
export const canvasCreateTool = new BaseTool({
  id: "canvas_create",
  name: "Canvas Create",
  description:
    "Create new canvas content from scratch or based on user query with intelligent content generation",
  category: "canvas",
  icon: "plus",
  runInstructions:
    "Generate comprehensive canvas content based on user query or provided content",
  type: "function",
  definition: {
    type: "function",
    function: {
      name: "canvas_create",
      description: "Create new canvas content with intelligent generation",
      parameters: {
        type: "object",
        properties: {
          content: {
            type: "string",
            description: "Direct content to use (optional if query provided)",
          },
          query: {
            type: "string",
            description:
              "User query for content generation (use this for AI-generated content)",
          },
          name: {
            type: "string",
            description: "Canvas name (auto-generated if not provided)",
          },
          type: {
            type: "string",
            enum: ["text", "code", "markdown", "html", "spreadsheet"],
            default: "text",
            description: "Content type",
          },
          language: {
            type: "string",
            default: "javascript",
            description: "Programming language for code content",
          },
          version: {
            type: "number",
            default: 1,
            description: "Canvas version",
          },
          useStreaming: {
            type: "boolean",
            default: true,
            description: "Use streaming for content generation",
          },
          sourceMessageId: {
            type: "string",
            description:
              "ID of the message that triggered this canvas creation",
          },
        },
        required: [],
        additionalProperties: false,
      },
    },
  },
  execute: async ({
    content,
    query,
    name,
    type = "text",
    language = "javascript",
    version = 1,
    useStreaming = true,
    sourceMessageId,
  }) => {
    try {
      // Auto-detect content type from query if query is provided and type is default
      if (query && type === "text") {
        type = detectContentTypeFromQuery(query);
        if (type === "code" && language === "javascript") {
          language = detectLanguageFromQuery(query);
        }
      }

      // Auto-generate name if not provided
      if (!name && query) {
        name = generateNameFromQuery(query, type);
      } else if (!name && content) {
        name = generateNameFromQuery(content.substring(0, 100), type);
      } else if (!name) {
        name = `New ${type} Canvas`;
      }

      // If useStreaming is enabled and we have a query, emit canvas_create event first
      if (useStreaming && query && sourceMessageId) {
        // Emit canvas creation event immediately with sourceMessageId
        canvasEventEmitter.emit("canvas_create", {
          content: "",
          type,
          language,
          name,
          version,
          sourceMessageId,
          metadata: {
            name,
            version,
            createdAt: new Date().toISOString(),
            lastModified: new Date().toISOString(),
            contentLength: 0,
            sourceMessageId,
            isStreaming: true,
          },
          canvasData: {
            content: "",
            contentType: type,
            type: type,
            language: language,
            name: name,
            title: name,
            version: version,
            sourceMessageId,
            metadata: {
              name,
              version,
              createdAt: new Date().toISOString(),
              lastModified: new Date().toISOString(),
              contentLength: 0,
              sourceMessageId,
              isStreaming: true,
            },
          },
        });
      }

      let finalContent = "";

      // If useStreaming is enabled and we have a query, use streaming
      if (useStreaming && query) {
        try {
          finalContent = await createMarkdownStream(
            query,
            type,
            language,
            name,
            version,
            sourceMessageId
          );
        } catch (streamError) {
          console.error(
            "🎨 Markdown streaming failed, falling back to direct content:",
            streamError
          );
          // Fallback to direct content
          finalContent =
            content || query || "Generated content based on your request";
        }
      } else {
        // Use direct content
        finalContent = content || query || "New canvas content";
      }

      // Update global canvas state
      globalCanvasState = {
        isOpen: true,
        content: finalContent,
        type,
        language,
        isDirty: true,
        metadata: {
          name,
          version,
          createdAt: new Date().toISOString(),
          lastModified: new Date().toISOString(),
          contentLength: finalContent.length,
          sourceMessageId,
        },
      };

      // Emit final canvas creation event with sourceMessageId (if not already emitted)
      if (!useStreaming || !query || !sourceMessageId) {
        canvasEventEmitter.emit("canvas_create", {
          content: finalContent,
          type,
          language,
          name,
          version,
          sourceMessageId,
          metadata: globalCanvasState.metadata,
          canvasData: {
            content: finalContent,
            contentType: type,
            type: type,
            language: language,
            name: name,
            title: name,
            version: version,
            sourceMessageId,
          },
        });
      }

      // Get provider info for metadata
      const modelInfo = getCurrentModelInfo();
      const { model: usedModel, provider: usedProvider } = modelInfo || {
        model: "unknown",
        provider: "unknown",
      };

      return {
        success: true,
        message: `Canvas "${name}" created successfully`,
        canvas: {
          name,
          type,
          language,
          version,
          content: finalContent,
          metadata: globalCanvasState.metadata,
        },
        // Add canvasData for persistence in tool_calls
        canvasData: {
          content: finalContent,
          contentType: type,
          type: type,
          language: language,
          name: name,
          title: name,
          version: version,
          sourceMessageId,
          metadata: {
            ...globalCanvasState.metadata,
            contentLength: finalContent.length,
            createdWith: "backend_streaming",
            toolType: "canvas_create",
            provider: usedProvider,
            model: usedModel,
            sourceMessageId,
          },
        },
      };
    } catch (error) {
      console.error("🎨 Canvas Create Tool error:", error);
      return {
        success: false,
        message: `Failed to create canvas: ${error.message}`,
        error: error.message,
        // Still provide canvasData even on error for any partial content
        canvasData: {
          content: content || query || "",
          contentType: type,
          type: type,
          language: language,
          name: name || "Failed Canvas",
          title: name || "Failed Canvas",
          version: version,
          sourceMessageId,
          metadata: {
            error: error.message,
            toolType: "canvas_create",
            failed: true,
            sourceMessageId,
          },
        },
      };
    }
  },
  options: {
    timeout: 120000, // 2 minutes for canvas generation
  },
});

/**
 * Canvas Edit Tool - Edits existing canvas content with markdown streaming
 */
export const canvasEditTool = new BaseTool({
  id: "canvas_edit",
  name: "Canvas Edit",
  description:
    "Edit existing canvas content based on user instructions while preserving structure",
  category: "canvas",
  icon: "edit",
  runInstructions:
    "Apply intelligent edits to existing canvas content based on user query",
  type: "function",
  definition: {
    type: "function",
    function: {
      name: "canvas_edit",
      description: "Edit existing canvas content based on user instructions",
      parameters: {
        type: "object",
        properties: {
          editQuery: {
            type: "string",
            description: "User query describing the desired changes",
          },
          editType: {
            type: "string",
            enum: ["add", "modify", "remove", "restructure", "fix", "refactor"],
            default: "modify",
            description: "Type of edit to perform",
          },
          targetSection: {
            type: "string",
            description: "Specific section or element to target (optional)",
          },
          useStreaming: {
            type: "boolean",
            default: true,
            description: "Use streaming for intelligent edits",
          },
          sourceMessageId: {
            type: "string",
            description: "ID of the message that triggered this canvas edit",
          },
        },
        required: ["editQuery"],
        additionalProperties: false,
      },
    },
  },
  execute: async ({
    editQuery,
    editType = "modify",
    targetSection,
    useStreaming = true,
    sourceMessageId,
  }) => {
    try {
      if (!globalCanvasState.content) {
        return {
          success: false,
          message: "No canvas content to edit. Create a canvas first.",
          canvasData: {
            content: "",
            contentType: "text",
            type: "text",
            language: "javascript",
            name: "No Canvas",
            title: "No Canvas",
            version: 1,
            sourceMessageId,
            metadata: {
              error: "No canvas content to edit",
              toolType: "canvas_edit",
              failed: true,
              sourceMessageId,
            },
          },
        };
      }

      const canvasMetadata = globalCanvasState.metadata || {};
      let editedContent = "";

      // If useStreaming is enabled, use streaming for intelligent edits
      if (useStreaming) {
        try {
          editedContent = await createMarkdownEditStream(
            editQuery,
            globalCanvasState.type || "text",
            globalCanvasState.language || "javascript",
            canvasMetadata.name || "Edited Canvas",
            (canvasMetadata.version || 1) + 1,
            editQuery,
            editType,
            globalCanvasState.content,
            sourceMessageId
          );
        } catch (streamError) {
          console.error(
            "�� Markdown streaming edit failed, falling back:",
            streamError
          );
          // Fallback to simple text-based edit
          editedContent =
            globalCanvasState.content + `\n\n[Edit: ${editQuery}]`;
        }
      } else {
        // Simple edit fallback
        editedContent = globalCanvasState.content + `\n\n[Edit: ${editQuery}]`;
      }

      // Update global canvas state
      const newVersion = (canvasMetadata.version || 1) + 1;
      globalCanvasState = {
        ...globalCanvasState,
        content: editedContent,
        isDirty: true,
        metadata: {
          ...canvasMetadata,
          version: newVersion,
          lastModified: new Date().toISOString(),
          contentLength: editedContent.length,
          sourceMessageId: sourceMessageId || canvasMetadata.sourceMessageId,
          lastEdit: {
            query: editQuery,
            type: editType,
            timestamp: new Date().toISOString(),
          },
        },
      };

      // Emit canvas edit event with sourceMessageId
      canvasEventEmitter.emit("canvas_edit", {
        content: editedContent,
        editQuery,
        editType,
        targetSection,
        version: newVersion,
        sourceMessageId: sourceMessageId || canvasMetadata.sourceMessageId,
        metadata: globalCanvasState.metadata,
      });

      // Get provider info for metadata
      const modelInfo = getCurrentModelInfo();
      const { model: usedModel, provider: usedProvider } = modelInfo || {
        model: "unknown",
        provider: "unknown",
      };

      return {
        success: true,
        message: `Canvas edited successfully (v${newVersion})`,
        canvas: {
          ...globalCanvasState,
          editSummary: {
            query: editQuery,
            type: editType,
            targetSection,
            changesApplied: true,
          },
        },
        // Add canvasData for persistence in tool_calls
        canvasData: {
          content: editedContent,
          contentType: globalCanvasState.type || "text",
          type: globalCanvasState.type || "text",
          language: globalCanvasState.language || "javascript",
          name: canvasMetadata.name || "Edited Canvas",
          title: canvasMetadata.name || "Edited Canvas",
          version: newVersion,
          sourceMessageId: sourceMessageId || canvasMetadata.sourceMessageId,
          metadata: {
            ...globalCanvasState.metadata,
            contentLength: editedContent.length,
            editedWith: "backend_streaming",
            toolType: "canvas_edit",
            provider: usedProvider,
            model: usedModel,
            sourceMessageId: sourceMessageId || canvasMetadata.sourceMessageId,
            lastEdit: {
              query: editQuery,
              type: editType,
              timestamp: new Date().toISOString(),
            },
          },
        },
      };
    } catch (error) {
      console.error("🎨 Canvas Edit Tool error:", error);
      return {
        success: false,
        message: `Failed to edit canvas: ${error.message}`,
        error: error.message,
        // Still provide canvasData even on error for any partial content
        canvasData: {
          content: globalCanvasState.content || "",
          contentType: globalCanvasState.type || "text",
          type: globalCanvasState.type || "text",
          language: globalCanvasState.language || "javascript",
          name: globalCanvasState.metadata?.name || "Failed Edit",
          title: globalCanvasState.metadata?.name || "Failed Edit",
          version: (globalCanvasState.metadata?.version || 1) + 1,
          sourceMessageId,
          metadata: {
            error: error.message,
            toolType: "canvas_edit",
            failed: true,
            editQuery: editQuery,
            editType: editType,
            sourceMessageId,
          },
        },
      };
    }
  },
  options: {
    timeout: 120000, // 2 minutes for canvas editing
  },
});

// Export all canvas tools
export const canvasTools = [canvasCreateTool, canvasEditTool];

export default {
  canvasCreateTool,
  canvasEditTool,
  canvasTools,
};
